import * as Sentry from '@sentry/browser';

export const APP_RELEASE = __APP_RELEASE__;
const sampled = Math.random() < 0.1;
const metrics = new Map();
let enabled = false, transactions = 0, summaries = 0, routeStarted = 0;
const routes = new Set(['collapsible','logBook','drivers','travel-expenses','exports','voltages','temperatures','fuel-analysis','trip-analysis','my-settings','alarm-panel','login','password-reset']);
export function currentRoute() {
  const name = window.location.hash.replace(/^#\/?/, '').split(/[/?]/)[0];
  return routes.has(name) ? name : name ? 'other' : 'map';
}
export function recordMetric(name, value = 1) {
  name=`${currentRoute()}.${name}`;
  if (!enabled || !sampled || !Number.isFinite(value) || value < 0 || metrics.size >= 64 && !metrics.has(name)) return;
  const metric = metrics.get(name) || {count:0,total:0,max:0};
  metric.count++; metric.total += value; metric.max = Math.max(metric.max,value);
  metrics.set(name,metric);
}
export function startTiming(category, name) {
  const start = performance.now(), route = currentRoute();
  let finished = false;
  return (status = 'ok') => {
    if (finished) return; finished = true;
    const ms = performance.now()-start;
    recordMetric(category, ms);
    if (!enabled || !sampled || transactions >= 100) return;
    transactions++;
    try {
      const transaction = Sentry.startTransaction({name:`${category}/${name}`,op:category,startTimestamp:(Date.now()-ms)/1000});
      transaction.setTag('route',route);
      transaction.setTag('outcome',status);
      transaction.setMeasurement('duration',ms,'millisecond');
      transaction.finish();
    } catch (_) { /* Diagnostics must never break application work. */ }
  };
}
export function routePainted() {
  recordMetric('route.paint',performance.now()-routeStarted);
}
function flush() {
  if (!enabled || !sampled || !metrics.size || summaries >= 20) return;
  summaries++;
  try {
    const tx=Sentry.startTransaction({name:'ui.performance.summary',op:'ui.metrics'});
    tx.setTag('route',currentRoute());
    tx.setTag('viewport',window.innerWidth<=768?'mobile':'desktop');
    tx.setTag('visibility',document.visibilityState);
    tx.setData('metrics',Object.fromEntries(metrics));
    tx.finish();
  } catch (_) { /* No reporting recursion. */ }
  metrics.clear();
}
export function initPerformanceDiagnostics() {
  if (enabled) return () => {};
  enabled=true;
  Sentry.setTag('performance_sample',sampled?'yes':'no');
  const observers=[];
  const onHash=()=>{routeStarted=performance.now();};
  const onHide=()=>{if(document.visibilityState==='hidden')flush();};
  window.addEventListener('hashchange',onHash);
  document.addEventListener('visibilitychange',onHide);
  window.addEventListener('pagehide',flush);
  let interval;
  if(sampled) {
    const observe=(type,handler,extra={})=>{
      try {const observer=new PerformanceObserver(list=>{for(const entry of list.getEntries())handler(entry);});observer.observe({type,buffered:true,...extra});observers.push(observer);}catch(_){}
    };
    observe('longtask',e=>{if(document.visibilityState==='visible')recordMetric('main_thread.longtask',e.duration);});
    observe('event',e=>{if(e.interactionId)recordMetric('interaction.duration',e.duration);},{durationThreshold:40});
    observe('largest-contentful-paint',e=>recordMetric('page.lcp',e.startTime));
    observe('layout-shift',e=>{if(!e.hadRecentInput)recordMetric('layout.shift',e.value);});
    observe('navigation',e=>{recordMetric('page.ttfb',e.responseStart-e.requestStart);recordMetric('page.dom_ready',e.domContentLoadedEventEnd);});
    interval=window.setInterval(flush,300000);
  }
  return ()=>{flush();enabled=false;for(const o of observers)o.disconnect();clearInterval(interval);window.removeEventListener('hashchange',onHash);document.removeEventListener('visibilitychange',onHide);window.removeEventListener('pagehide',flush);metrics.clear();};
}

// Performance events contain no user identity, request body, query string,
// GraphQL variables, DOM text, tile URLs or geographic coordinates.
export function sanitizePerformanceEvent(event) {
  delete event.user; delete event.request; delete event.breadcrumbs;
  return event;
}
